import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  LinearProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Science as TestIcon,
  Restore as RestoreIcon,
  Update as UpdateIcon
} from '@mui/icons-material';
import newImportService from '../../services/newImportService';

const MappingAndUpdate = () => {
  // Shared State
  const [availableFields, setAvailableFields] = useState({
    allFields: [],
    systemFields: [],
    microsoftFields: [],
    customFields: []
  });
  const [selectedCategory, setSelectedCategory] = useState('systemFields');
  const [loadingFields, setLoadingFields] = useState(false);

  // Field Mapping State
  const [fieldMapping, setFieldMapping] = useState({
    commonFields: [],
    workItemTypeFields: {},
    dynamicFields: {}
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [mappingMessage, setMappingMessage] = useState({ type: '', text: '' });
  const [newField, setNewField] = useState('');
  const [expandedPanel, setExpandedPanel] = useState('common');

  // Quick Updates State
  const [selectedFields, setSelectedFields] = useState([]);
  const [selectedWorkItemTypes, setSelectedWorkItemTypes] = useState([]);
  const [workItemCounts, setWorkItemCounts] = useState({});
  const [updating, setUpdating] = useState(false);
  const [updateMessage, setUpdateMessage] = useState({ type: '', text: '' });
  const [updateProgress, setUpdateProgress] = useState(null);

  // Work item types
  const workItemTypes = [
    'Epic',
    'Feature',
    'Product Backlog Item',
    'Bug',
    'Task',
    'Test'
  ];

  const quickUpdateWorkItemTypes = [
    { value: 'Epic', label: 'Epic', collection: 'WorkItem' },
    { value: 'Feature', label: 'Feature', collection: 'WorkItem' },
    { value: 'Product Backlog Item', label: 'PBI', collection: 'WorkItem' },
    { value: 'Bug', label: 'Bug', collection: 'WorkItem' },
    { value: 'Task', label: 'Task', collection: 'WorkItem' },
    { value: 'Test', label: 'Test', collection: 'Test' }
  ];

  // Shared: Load available fields from ADO
  const loadAvailableFields = async () => {
    try {
      setLoadingFields(true);
      // Use getAvailableAdoFields (used by Field Mapping) - both endpoints should return same structure
      const response = await newImportService.getAvailableAdoFields();
      if (response.success) {
        setAvailableFields(response.data);
      } else {
        // Fallback to Quick Updates endpoint if primary fails
        const fallbackResponse = await newImportService.getQuickUpdateAvailableFields();
        if (fallbackResponse.success) {
          setAvailableFields(fallbackResponse.data);
        } else {
          setMappingMessage({ type: 'error', text: response.message || 'Failed to load available fields' });
          setUpdateMessage({ type: 'error', text: response.message || 'Failed to load available fields' });
        }
      }
    } catch (error) {
      setMappingMessage({ type: 'error', text: 'Failed to load available fields from ADO' });
      setUpdateMessage({ type: 'error', text: 'Failed to load available fields from ADO' });
    } finally {
      setLoadingFields(false);
    }
  };

  // Field Mapping: Load field mapping configuration
  const loadFieldMapping = async () => {
    try {
      setLoadingFields(true);
      const response = await newImportService.getAdoFieldMapping();
      if (response.success) {
        setFieldMapping(response.data);
      } else {
        setMappingMessage({ type: 'error', text: response.message });
      }
    } catch (error) {
      setMappingMessage({ type: 'error', text: 'Failed to load field mapping configuration' });
    } finally {
      setLoadingFields(false);
    }
  };

  // Field Mapping: Save field mapping configuration
  const saveFieldMapping = async () => {
    try {
      setSaving(true);
      const response = await newImportService.saveAdoFieldMapping(fieldMapping);
      if (response.success) {
        let text = 'Field mapping configuration saved successfully';
        if (response.backfill && response.backfill.importLogId) {
          text += ' — Backfill started.';
        }
        setMappingMessage({ type: 'success', text });
      } else {
        setMappingMessage({ type: 'error', text: response.message });
      }
    } catch (error) {
      setMappingMessage({ type: 'error', text: 'Failed to save field mapping configuration' });
    } finally {
      setSaving(false);
    }
  };

  // Field Mapping: Test field availability
  const testFields = async (fields) => {
    try {
      setTesting(true);
      const response = await newImportService.testAdoFieldMapping(fields);
      if (response.success) {
        setMappingMessage({
          type: 'success',
          text: `All ${fields.length} fields are available in ADO`
        });
      } else {
        setMappingMessage({
          type: 'warning',
          text: `Some fields are not available: ${response.data.unavailableFields.join(', ')}`
        });
      }
    } catch (error) {
      setMappingMessage({ type: 'error', text: 'Failed to test field availability' });
    } finally {
      setTesting(false);
    }
  };

  // Field Mapping: Reset to default configuration
  const resetToDefault = async () => {
    try {
      setSaving(true);
      const response = await newImportService.resetAdoFieldMapping();
      if (response.success) {
        setFieldMapping(response.data);
        setMappingMessage({ type: 'success', text: 'Field mapping reset to default configuration' });
      } else {
        setMappingMessage({ type: 'error', text: response.message });
      }
    } catch (error) {
      setMappingMessage({ type: 'error', text: 'Failed to reset field mapping configuration' });
    } finally {
      setSaving(false);
    }
  };

  // Field Mapping: Apply Full Import defaults (align Mapping with Full Import)
  const resetToFullImportDefaults = async () => {
    try {
      setSaving(true);
      const response = await newImportService.resetAdoFieldMappingToFullImport();
      if (response.success) {
        setFieldMapping(response.data);
        let text = 'Field mapping set to Full Import defaults';
        if (response.backfill && response.backfill.importLogId) {
          text += ' — Backfill started.';
        }
        setMappingMessage({ type: 'success', text });
      } else {
        setMappingMessage({ type: 'error', text: response.message });
      }
    } catch (error) {
      setMappingMessage({ type: 'error', text: 'Failed to apply Full Import defaults' });
    } finally {
      setSaving(false);
    }
  };

  // Field Mapping: Add field to common fields
  const addCommonField = () => {
    if (newField && !fieldMapping.commonFields.includes(newField)) {
      setFieldMapping(prev => ({
        ...prev,
        commonFields: [...prev.commonFields, newField]
      }));
      setNewField('');
    }
  };

  // Field Mapping: Remove field from common fields
  const removeCommonField = (field) => {
    setFieldMapping(prev => ({
      ...prev,
      commonFields: prev.commonFields.filter(f => f !== field)
    }));
  };

  // Field Mapping: Add field to work item type
  const addWorkItemTypeField = (workItemType, field) => {
    if (field && !fieldMapping.workItemTypeFields[workItemType]?.includes(field)) {
      setFieldMapping(prev => ({
        ...prev,
        workItemTypeFields: {
          ...prev.workItemTypeFields,
          [workItemType]: [...(prev.workItemTypeFields[workItemType] || []), field]
        }
      }));
    }
  };

  // Field Mapping: Remove field from work item type
  const removeWorkItemTypeField = (workItemType, field) => {
    setFieldMapping(prev => ({
      ...prev,
      workItemTypeFields: {
        ...prev.workItemTypeFields,
        [workItemType]: (prev.workItemTypeFields[workItemType] || []).filter(f => f !== field)
      }
    }));
  };

  // Quick Updates: Load work item counts
  const loadWorkItemCounts = async () => {
    try {
      const response = await newImportService.getQuickUpdateCounts();
      if (response.success) {
        setWorkItemCounts(response.data || {});
      }
    } catch (error) {
      console.error('Failed to load work item counts:', error);
    }
  };

  // Quick Updates: Add field to selection
  const addField = (field) => {
    if (field && !selectedFields.includes(field)) {
      setSelectedFields([...selectedFields, field]);
    }
  };

  // Quick Updates: Remove field from selection
  const removeField = (field) => {
    setSelectedFields(selectedFields.filter(f => f !== field));
  };

  // Quick Updates: Perform quick update
  const performUpdate = async () => {
    if (selectedFields.length === 0) {
      setUpdateMessage({ type: 'error', text: 'Please select at least one field to update' });
      return;
    }

    if (selectedWorkItemTypes.length === 0) {
      setUpdateMessage({ type: 'error', text: 'Please select at least one work item type to update' });
      return;
    }

    try {
      setUpdating(true);
      setUpdateMessage({ type: '', text: '' });
      setUpdateProgress({ status: 'running', message: 'Starting update...' });

      const response = await newImportService.performQuickUpdate({
        fields: selectedFields,
        workItemTypes: selectedWorkItemTypes
      });

      if (response.success) {
        const data = response.data || {};
        const totalUpdated = data.totalUpdated || 0;
        const totalFailed = data.totalFailed || 0;
        const totalProcessed = data.totalProcessed || 0;

        setUpdateMessage({
          type: 'success',
          text: `Update completed! ${totalUpdated} items updated, ${totalFailed} failed out of ${totalProcessed} processed.`
        });

        setUpdateProgress({
          status: 'completed',
          message: `Update completed successfully`,
          details: data
        });

        // Reload counts after update
        await loadWorkItemCounts();
      } else {
        setUpdateMessage({
          type: 'error',
          text: response.message || 'Update failed'
        });
        setUpdateProgress({
          status: 'error',
          message: response.message || 'Update failed'
        });
      }
    } catch (error) {
      setUpdateMessage({
        type: 'error',
        text: 'Failed to perform quick update: ' + (error.message || 'Unknown error')
      });
      setUpdateProgress({
        status: 'error',
        message: error.message || 'Update failed'
      });
    } finally {
      setUpdating(false);
    }
  };

  // Get fields for selected category
  const getFieldsForCategory = () => {
    switch (selectedCategory) {
      case 'systemFields':
        return availableFields.systemFields || [];
      case 'microsoftFields':
        return availableFields.microsoftFields || [];
      case 'customFields':
        return availableFields.customFields || [];
      default:
        return availableFields.allFields || [];
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadFieldMapping();
    loadAvailableFields();
    loadWorkItemCounts();
  }, []);

  return (
    <Box sx={{ width: '100%', overflow: 'hidden', p: 2 }}>
      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          Mapping & Update
        </Typography>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Configure ADO field mappings and perform quick updates across work item collections.
        </Typography>

        {mappingMessage.text && (
          <Alert 
            severity={mappingMessage.type} 
            sx={{ mb: 2 }}
            onClose={() => setMappingMessage({ type: '', text: '' })}
          >
            {mappingMessage.text}
          </Alert>
        )}

        {updateMessage.text && (
          <Alert 
            severity={updateMessage.type === 'error' ? 'error' : updateMessage.type === 'success' ? 'success' : 'info'}
            sx={{ mb: 2 }}
            onClose={() => setUpdateMessage({ type: '', text: '' })}
          >
            {updateMessage.text}
          </Alert>
        )}

        {updateProgress && updateProgress.status === 'running' && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {updateProgress.message}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Main Content Grid */}
      <Grid container spacing={3} sx={{ width: '100%' }}>
        {/* Shared Available Fields Panel */}
        <Grid item xs={12} lg={5}>
          <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Available Fields from ADO
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  size="small"
                >
                  <MenuItem value="allFields">All Fields ({availableFields.allFields?.length || 0})</MenuItem>
                  <MenuItem value="systemFields">System Fields ({availableFields.systemFields?.length || 0})</MenuItem>
                  <MenuItem value="microsoftFields">Microsoft Fields ({availableFields.microsoftFields?.length || 0})</MenuItem>
                  <MenuItem value="customFields">Custom Fields ({availableFields.customFields?.length || 0})</MenuItem>
                </Select>
              </FormControl>

              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadAvailableFields}
                disabled={loadingFields}
                fullWidth
                sx={{ mb: 2 }}
                size="small"
              >
                {loadingFields ? <CircularProgress size={16} /> : 'Refresh Fields'}
              </Button>

              <Paper 
                sx={{ 
                  flex: 1, 
                  overflow: 'auto', 
                  minHeight: '400px',
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <List dense sx={{ p: 1 }}>
                  {getFieldsForCategory().map((field) => (
                    <ListItem 
                      key={field} 
                      sx={{ 
                        py: 0.5, 
                        px: 1,
                        '&:hover': { 
                          backgroundColor: 'action.hover',
                          borderRadius: 1
                        } 
                      }}
                    >
                      <ListItemText 
                        primary={field} 
                        primaryTypographyProps={{ 
                          fontSize: '0.8rem',
                          fontFamily: 'monospace',
                          noWrap: false,
                          sx: {
                            wordBreak: 'break-all',
                            lineHeight: 1.2
                          }
                        }} 
                        sx={{ 
                          pr: 8,
                          '& .MuiListItemText-primary': {
                            overflow: 'visible',
                            whiteSpace: 'normal'
                          }
                        }}
                      />
                      <ListItemSecondaryAction>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={() => setNewField(field)}
                            title="Add to Field Mapping"
                            sx={{ 
                              '&:hover': { 
                                backgroundColor: 'primary.main',
                                color: 'primary.contrastText'
                              }
                            }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => addField(field)}
                            disabled={selectedFields.includes(field)}
                            title="Add to Quick Updates"
                            sx={{ 
                              '&:hover': { 
                                backgroundColor: 'secondary.main',
                                color: 'secondary.contrastText'
                              }
                            }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Panel: Field Mapping and Quick Updates */}
        <Grid item xs={12} lg={7}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Field Mapping Configuration Section */}
            <Card>
              <CardContent>
                {/* Action Buttons */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  mb: 3,
                  flexWrap: 'wrap',
                  gap: 2
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Field Mapping Configuration
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      startIcon={<TestIcon />}
                      onClick={() => testFields(fieldMapping.commonFields)}
                      disabled={testing || fieldMapping.commonFields.length === 0}
                      size="small"
                    >
                      {testing ? <CircularProgress size={16} /> : 'Test Fields'}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<RestoreIcon />}
                      onClick={resetToDefault}
                      disabled={saving}
                      size="small"
                    >
                      Reset
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={resetToFullImportDefaults}
                      disabled={saving}
                      size="small"
                      title="Set common and type-specific fields to match Full Import"
                    >
                      Use Full Import defaults
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={saveFieldMapping}
                      disabled={saving}
                      size="small"
                    >
                      {saving ? <CircularProgress size={16} /> : 'Save'}
                    </Button>
                  </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Configure which fields to fetch from Azure DevOps for different work item types.
                </Typography>

                {/* Field Configuration Sections */}
                <Box>
                  {/* Common Fields */}
                  <Accordion 
                    expanded={expandedPanel === 'common'} 
                    onChange={() => setExpandedPanel(expandedPanel === 'common' ? '' : 'common')}
                    sx={{ mb: 1 }}
                  >
                    <AccordionSummary 
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ 
                        backgroundColor: 'primary.light',
                        color: 'primary.contrastText',
                        '&:hover': { backgroundColor: 'primary.main' }
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Common Fields ({fieldMapping.commonFields.length})
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Fields that apply to all work item types
                      </Typography>
                      
                      <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                        <TextField
                          size="small"
                          placeholder="Add field name"
                          value={newField}
                          onChange={(e) => setNewField(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && addCommonField()}
                          sx={{ flex: 1 }}
                        />
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={addCommonField}
                          disabled={!newField}
                          size="small"
                        >
                          Add
                        </Button>
                      </Box>

                      <Box sx={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: 1,
                        minHeight: '40px',
                        p: 1,
                        border: '1px dashed',
                        borderColor: 'divider',
                        borderRadius: 1
                      }}>
                        {fieldMapping.commonFields.length === 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No common fields configured
                          </Typography>
                        ) : (
                          fieldMapping.commonFields.map((field) => (
                            <Chip
                              key={field}
                              label={field}
                              onDelete={() => removeCommonField(field)}
                              color="primary"
                              variant="outlined"
                              size="small"
                              sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                            />
                          ))
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>

                  {/* Work Item Type Fields */}
                  {workItemTypes.map((workItemType, index) => (
                    <Accordion 
                      key={workItemType}
                      expanded={expandedPanel === workItemType} 
                      onChange={() => setExpandedPanel(expandedPanel === workItemType ? '' : workItemType)}
                      sx={{ mb: 1 }}
                    >
                      <AccordionSummary 
                        expandIcon={<ExpandMoreIcon />}
                        sx={{ 
                          backgroundColor: index % 2 === 0 ? 'grey.50' : 'grey.100',
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                      >
                        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                          {workItemType} ({
                            (fieldMapping.workItemTypeFields[workItemType]?.length || 0) + 
                            (fieldMapping.dynamicFields[workItemType]?.length || 0)
                          })
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Type-specific fields for {workItemType}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                          <TextField
                            size="small"
                            placeholder="Add field name"
                            value={newField}
                            onChange={(e) => setNewField(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && addWorkItemTypeField(workItemType, newField)}
                            sx={{ flex: 1 }}
                          />
                          <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() => addWorkItemTypeField(workItemType, newField)}
                            disabled={!newField}
                            size="small"
                          >
                            Add
                          </Button>
                        </Box>

                        {/* Combined display of workItemTypeFields and dynamicFields */}
                        <Box sx={{ 
                          display: 'flex', 
                          flexWrap: 'wrap', 
                          gap: 1,
                          minHeight: '40px',
                          p: 1,
                          border: '1px dashed',
                          borderColor: 'divider',
                          borderRadius: 1
                        }}>
                          {(() => {
                            const typeFields = fieldMapping.workItemTypeFields[workItemType] || [];
                            const dynamicFields = fieldMapping.dynamicFields[workItemType] || [];
                            const allFields = [...typeFields, ...dynamicFields];
                            
                            if (allFields.length === 0) {
                              return (
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                  No specific fields configured for {workItemType}
                                </Typography>
                              );
                            }
                            
                            return allFields.map((field) => {
                              const isDynamic = dynamicFields.includes(field);
                              return (
                                <Chip
                                  key={field}
                                  label={field}
                                  onDelete={isDynamic ? undefined : () => removeWorkItemTypeField(workItemType, field)}
                                  color={isDynamic ? "success" : "secondary"}
                                  variant="outlined"
                                  size="small"
                                  sx={{ 
                                    fontFamily: 'monospace', 
                                    fontSize: '0.75rem',
                                    ...(isDynamic && { 
                                      borderStyle: 'dashed',
                                      opacity: 0.8
                                    })
                                  }}
                                  title={isDynamic ? 'Added via Quick Update (read-only)' : 'Type-specific field'}
                                />
                              );
                            });
                          })()}
                        </Box>
                        
                        {/* Show dynamic fields info if they exist */}
                        {(fieldMapping.dynamicFields[workItemType] || []).length > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                            Note: Fields with dashed border were added via Quick Updates and will be included in future imports
                          </Typography>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              </CardContent>
            </Card>

            <Divider />

            {/* Quick Updates Section */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                  Quick Updates
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Select ADO fields to update across work item collections. Fields will be fetched from ADO in batches of 200 and updated in MongoDB.
                </Typography>

                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                  Selected Fields ({selectedFields.length})
                </Typography>

                <Box sx={{ mb: 3, minHeight: '100px', maxHeight: '200px', overflow: 'auto', p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  {selectedFields.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No fields selected. Select fields from the left panel.
                    </Typography>
                  ) : (
                    selectedFields.map((field) => (
                      <Chip
                        key={field}
                        label={field}
                        onDelete={() => removeField(field)}
                        color="primary"
                        variant="outlined"
                        size="small"
                        sx={{ m: 0.5, fontFamily: 'monospace', fontSize: '0.75rem' }}
                      />
                    ))
                  )}
                </Box>

                <Divider sx={{ my: 3 }} />

                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                  Work Items to Update
                </Typography>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {quickUpdateWorkItemTypes.map((type) => (
                    <Grid item xs={12} sm={6} md={4} key={type.value}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedWorkItemTypes.includes(type.value)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedWorkItemTypes([...selectedWorkItemTypes, type.value]);
                              } else {
                                setSelectedWorkItemTypes(selectedWorkItemTypes.filter(t => t !== type.value));
                              }
                            }}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2">{type.label}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {workItemCounts[type.value] || 0} items
                            </Typography>
                          </Box>
                        }
                      />
                    </Grid>
                  ))}
                </Grid>

                <Button
                  variant="contained"
                  color="primary"
                  startIcon={updating ? <CircularProgress size={16} color="inherit" /> : <UpdateIcon />}
                  onClick={performUpdate}
                  disabled={updating || selectedFields.length === 0 || selectedWorkItemTypes.length === 0}
                  fullWidth
                  size="large"
                  sx={{ mt: 2 }}
                >
                  {updating ? 'Updating...' : 'Update Work Items'}
                </Button>

                {updateProgress && updateProgress.details && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Update Results by Work Item Type:
                    </Typography>
                    {Object.entries(updateProgress.details.workItemTypes || {}).map(([workItemType, stats]) => (
                      <Box key={workItemType} sx={{ mb: 1 }}>
                        <Typography variant="body2">
                          <strong>{workItemType}:</strong> {stats.updated || 0} updated, {stats.failed || 0} failed out of {stats.processed || 0} processed
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MappingAndUpdate;

